// ---------------------------------------------------------------------------
// 損益分岐点分析ページ (実質家賃 / 市場家賃) — 2026-06-25
//   「あとどれだけ売れば黒字か / どれだけ余裕があるか」を 1 画面で示す経営管理ダッシュボード。
//   会社=クマガイコーポレーション (freee company_id=11518223)、80期=2025-10〜2026-09。
//   データは worker /api/breakeven/report が freee(GET読取) + D1市場家賃 + ROUMU を集約して返す。
//   クライアントは描画 + 実質⇄市場家賃の派生切替のみ (再 fetch なし)。
//
//   構成: 操作バー → KPIカード6枚 → 主役チャート(横ゲージ+安全余裕円弧) →
//         CVP図 + 売上分解 → 店舗別達成度ランキング → (折りたたみ)損益構造テーブル。
//   配色: 黒字緑 #2da663 / 赤字橙〜赤 #c4732a〜#dc2626 / 分岐点グレー破線 #6b7280 / 市場家賃モード紫 #7c3aed。
//   絵文字禁止・react-icons/md のみ・@freee_jp/vibes 準拠・tabular-nums・▲赤字・@media print 簡易対応。
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container, PageTitle, CardBase, HStack, Button, Note,
  Breadcrumbs, MessageBlock, InlineSpinner, SelectBox,
} from '@freee_jp/vibes';
import {
  MdTrendingUp, MdRefresh, MdWarning, MdSpeed, MdAccountBalance,
  MdShowChart, MdSavings, MdQueryStats, MdSwapHoriz,
} from 'react-icons/md';
import {
  fetchBreakevenReport, BreakevenReport, BreakevenStore, BreakevenMetrics, BreakevenPeriod, BreakevenGroup,
} from '../api/client';
import { SCOPES, scopeByKey } from '../data/storeGroups';
import { MatrixColumn } from '../components/BreakevenMatrix';
import { BreakevenCards } from '../components/BreakevenCards';
import { HorizontalBreakevenGauge, SafetyArcGauge } from '../components/BreakevenGauge';
import { CvpChart, SalesDecomposition } from '../components/CvpChart';
import { StoreRankBars, StoreRankRow } from '../components/StoreRankBars';

// --- 色定数 ---
const GREEN = '#2da663';
const GREEN_DK = '#1f8a4c';
const ORANGE = '#c4732a';
const RED = '#dc2626';
const BLUE = '#2667d4';
const PURPLE = '#7c3aed';
const SUB = '#8a8580';

// --- localStorage 永続 ---
const LS = {
  rent: 'freeeman.breakeven.rentMode.v1',
  prev: 'freeeman.breakeven.showPrev.v1',
  scope: 'freeeman.breakeven.scope.v1',
  period: 'freeeman.breakeven.period.v1',
  view: 'freeeman.breakeven.view.v1',
};
const loadStr = (k: string, def: string): string => { try { return localStorage.getItem(k) ?? def; } catch { return def; } };
const saveStr = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* */ } };

// --- 書式 ---
const yenShort = (v: number | null): string => {
  if (v == null) return '—';
  const a = Math.abs(v);
  const sign = v < 0 ? '▲' : '';
  if (a >= 1e8) return `${sign}¥${(a / 1e8).toFixed(a >= 1e9 ? 0 : 2)}億`;
  if (a >= 1e6) return `${sign}¥${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e4) return `${sign}¥${Math.round(a / 1e3).toLocaleString('ja-JP')}千`;
  return `${sign}¥${Math.round(a).toLocaleString('ja-JP')}`;
};
const yenFull = (v: number | null): string =>
  v == null ? '—' : `${v < 0 ? '▲' : ''}¥${Math.abs(Math.round(v)).toLocaleString('ja-JP')}`;
const pct = (v: number | null, digits = 2): string => v == null ? '—' : `${(v * 100).toFixed(digits)}%`;

// --- 会計期 / 期間 helper ---
const TERM = 80;
const TERM_LABEL = '80期 (2025-10〜2026-09)';
// 会計月 1=10月 … 12=9月
const FISCAL_MONTHS: { fm: number; label: string; ym: (term: number) => string }[] = Array.from({ length: 12 }, (_, i) => {
  const fm = i + 1;
  const calMonth0 = 9 + i; // 10月始まり (0-based from Jan)
  const month = (calMonth0 % 12) + 1;
  const labels = ['10月', '11月', '12月', '1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月'];
  return {
    fm,
    label: labels[i],
    ym: (term: number) => {
      const startYear = 1945 + term;
      const y = startYear + Math.floor(calMonth0 / 12);
      return `${y}-${String(month).padStart(2, '0')}`;
    },
  };
});

// --- S 定数 (SeibuCashLedger / BudgetActual の流儀踏襲) ---
const S = {
  page: {
    background: 'linear-gradient(180deg, #f8f7f5 0%, #f1efec 100%)',
    minHeight: '100vh', paddingBottom: '4rem',
  } as React.CSSProperties,
  bar: { padding: '14px 18px' } as React.CSSProperties,
  ctlLabel: { fontSize: '0.6875rem', color: '#6b6560', fontWeight: 700, letterSpacing: '0.04em', marginBottom: 3 } as React.CSSProperties,
  kpiRow: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(178px, 1fr))',
    gap: '11px', marginTop: '14px',
  } as React.CSSProperties,
  kpiCard: (accent: string): React.CSSProperties => ({
    background: `linear-gradient(135deg, ${accent}16 0%, ${accent}06 100%)`,
    border: `1px solid ${accent}2e`, borderRadius: '13px', padding: '13px 16px',
    display: 'flex', flexDirection: 'column', minHeight: 92,
  }),
  kpiLabel: { fontSize: '0.6875rem', color: '#6b6560', fontWeight: 700, letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 4 } as React.CSSProperties,
  kpiValue: { fontSize: '1.4rem', fontWeight: 800, marginTop: '5px', fontVariantNumeric: 'tabular-nums', lineHeight: 1.15 } as React.CSSProperties,
  kpiSub: { fontSize: '0.6875rem', color: SUB, marginTop: 'auto', paddingTop: 4, fontVariantNumeric: 'tabular-nums' } as React.CSSProperties,
  cardPad: { padding: '16px 18px' } as React.CSSProperties,
  sectionTitle: { fontSize: '0.8125rem', fontWeight: 700, color: '#5c554f', letterSpacing: '0.02em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 } as React.CSSProperties,
};
const CHIP = (on: boolean, accent = BLUE): React.CSSProperties => ({
  padding: '5px 13px', borderRadius: 16, cursor: 'pointer', fontSize: '0.8125rem', whiteSpace: 'nowrap',
  border: on ? `1px solid ${accent}` : '1px solid #d4d2d0',
  background: on ? accent : '#fff', color: on ? '#fff' : '#333', fontWeight: on ? 700 : 500,
  userSelect: 'none', transition: 'all .12s',
});
const SEG = (on: boolean, accent = BLUE): React.CSSProperties => ({
  padding: '6px 16px', cursor: 'pointer', fontSize: '0.8125rem', whiteSpace: 'nowrap',
  background: on ? accent : '#fff', color: on ? '#fff' : '#555', fontWeight: on ? 700 : 500,
  userSelect: 'none', border: 'none',
});

type RentMode = 'market' | 'real';

// 指標を rent モードに応じて読み出すヘルパ。market_rent_missing 時は market 版を null に。
interface Derived {
  sales: number;
  material: number;
  costRatio: number;
  grossProfit: number;
  marginRatio: number;
  fixedCost: number;            // 一般管理費 (rent 反映)
  operatingProfit: number | null;  // rent 反映 (market かつ未投入なら null)
  breakeven: number | null;
  safety: number | null;
  achievement: number | null;
  workHours: number;
  laborProductivity: number | null;
  cfRequired: number | null;
  cf: number;
  bankRepayment: number;
  ordinaryProfit: number;
  depreciation: number;
  rentReal: number;
  rentMarket: number;
  rentDiff: number;
  marketMissing: boolean;
}

function derive(m: BreakevenMetrics, rent: RentMode, marketMissing: boolean): Derived {
  const useMarket = rent === 'market';
  // 市場家賃モードで未投入のときは market 系指標を未確定(null)にする。
  const blockMarket = useMarket && marketMissing;
  const fixedCost = useMarket ? m.一般管理費_市場 : m.一般管理費;
  const operatingProfit = useMarket ? m.営業利益_市場 : m.営業利益;
  const breakeven = useMarket ? m.損益分岐点_市場 : m.損益分岐点;
  return {
    sales: m.売上,
    material: m.原材料,
    costRatio: m.原価率,
    grossProfit: m.粗利益,
    marginRatio: 1 - m.原価率,
    fixedCost,
    operatingProfit: blockMarket ? null : operatingProfit,
    breakeven: blockMarket ? null : breakeven,
    safety: blockMarket ? null : (m.売上 > 0 && breakeven ? (m.売上 - breakeven) / m.売上 : null),
    achievement: blockMarket ? null : (breakeven > 0 ? m.売上 / breakeven : null),
    workHours: m.勤務時間,
    laborProductivity: m.勤務時間 > 0 ? m.人時生産性 : null,
    cfRequired: blockMarket ? null : m.CF必要売上,
    cf: m.CF,
    bankRepayment: m.銀行返済,
    ordinaryProfit: m.経常利益,
    depreciation: m.減価償却費,
    rentReal: m.地代家賃,
    rentMarket: m.市場家賃,
    rentDiff: m.家賃減額差,
    marketMissing,
  };
}

// 前年比 (金額の比率 or pt 差)
const yoyRatio = (cur: number | null, prior: number | null | undefined): number | null =>
  cur == null || prior == null || prior === 0 ? null : cur / prior - 1;

// グループ集計を worker が group 行で返さない場合のクライアント側合算 (加算可能な項目を section 横断で合算し、
// 比率/分岐点/CF必要売上 は合算後に再計算)。worker contract (DESIGN spec §7) が group 行を返せばそちらを優先。
function aggregateMetrics(list: BreakevenMetrics[]): BreakevenMetrics {
  const z = (): BreakevenMetrics => ({
    売上: 0, 原材料: 0, 原価率: 0, 粗利益: 0, 労務費: 0, 販売費: 0, 一般管理費: 0, 営業利益: 0,
    市場家賃: 0, 地代家賃: 0, 家賃減額差: 0, 一般管理費_市場: 0, 営業利益_市場: 0,
    損益分岐点: 0, 損益分岐点_市場: 0, 安全余裕率: 0, 達成率: 0, 勤務時間: 0, 人時生産性: 0,
    減価償却費: 0, 営業外収益: 0, 営業外費用: 0, 経常利益: 0, 銀行返済: 0, CF: 0, CF必要売上: 0,
  });
  const a = z();
  const ADD: (keyof BreakevenMetrics)[] = [
    '売上', '原材料', '粗利益', '労務費', '販売費', '一般管理費', '営業利益',
    '市場家賃', '地代家賃', '家賃減額差', '一般管理費_市場', '営業利益_市場',
    '勤務時間', '減価償却費', '営業外収益', '営業外費用', '経常利益', '銀行返済', 'CF',
  ];
  for (const m of list) for (const k of ADD) (a[k] as number) += m[k] || 0;
  a.原価率 = a.売上 > 0 ? a.原材料 / a.売上 : 0;
  const margin = 1 - a.原価率;
  a.損益分岐点 = margin > 0 ? a.一般管理費 / margin : 0;
  a.損益分岐点_市場 = margin > 0 ? a.一般管理費_市場 / margin : 0;
  a.安全余裕率 = a.売上 > 0 && a.損益分岐点 ? (a.売上 - a.損益分岐点) / a.売上 : 0;
  a.達成率 = a.損益分岐点 > 0 ? a.売上 / a.損益分岐点 : 0;
  a.人時生産性 = a.勤務時間 > 0 ? a.粗利益 / a.勤務時間 : 0;
  a.CF必要売上 = margin > 0
    ? (a.一般管理費 + a.銀行返済 - a.減価償却費 - a.営業外収益 + a.営業外費用) / margin : 0;
  return a;
}

export const BreakEvenPage: React.FC = () => {
  const [period, setPeriod] = useState<BreakevenPeriod>(() => (loadStr(LS.period, 'month') as BreakevenPeriod));
  const [fm, setFm] = useState<number>(7); // 既定: 4月(fm7) ※月末仕訳(市場家賃)まで投入済の直近締め月。5月以降は未投入で市場版が未確定になるため既定から外す。通期では非活性。
  const [rent, setRent] = useState<RentMode>(() => (loadStr(LS.rent, 'market') as RentMode));
  const [showPrev, setShowPrev] = useState<boolean>(() => loadStr(LS.prev, 'off') === 'on');
  const [scopeKey, setScopeKey] = useState<string>(() => loadStr(LS.scope, 'company'));
  const [drillStore, setDrillStore] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'analysis' | 'table'>(() => (loadStr(LS.view, 'analysis') === 'table' ? 'table' : 'analysis'));

  const [report, setReport] = useState<BreakevenReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { saveStr(LS.rent, rent); }, [rent]);
  useEffect(() => { saveStr(LS.prev, showPrev ? 'on' : 'off'); }, [showPrev]);
  useEffect(() => { saveStr(LS.scope, scopeKey); }, [scopeKey]);
  useEffect(() => { saveStr(LS.period, period); }, [period]);
  useEffect(() => { saveStr(LS.view, viewMode); }, [viewMode]);

  const endMonth = useMemo(() => {
    if (period === 'full') return FISCAL_MONTHS[11].ym(TERM); // 通期末=9月
    return FISCAL_MONTHS[fm - 1].ym(TERM);
  }, [period, fm]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetchBreakevenReport({ period, end_month: endMonth, compare_prev: showPrev, include_ec: false });
      setReport(r);
    } catch (err: any) {
      setError(err?.message || '読み込みに失敗しました');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [period, endMonth, showPrev]);

  useEffect(() => { load(); }, [load]);

  // --- スコープ解決 (グループ or ドリル先店舗) ---
  const activeScope = scopeByKey(scopeKey) || SCOPES[0];

  // worker レスポンスから store/group を探す。
  const allStores: BreakevenStore[] = useMemo(() => {
    if (!report) return [];
    const out: BreakevenStore[] = [];
    if (report.company) out.push(report.company);
    for (const g of report.groups || []) for (const s of g.stores) out.push(s);
    return out;
  }, [report]);

  // 単店 (store:<sid> / 山城屋合算 sentinel) を worker レスポンスから解決。
  const resolveSingleStore = useCallback((key: string): BreakevenStore | undefined => {
    if (!report) return undefined;
    const def = scopeByKey(key);
    const wantSids = def && def.isStore ? def.sectionIds : (key.startsWith('store:') ? [Number(key.slice(6))] : []);
    const byKey = (g: BreakevenStore): boolean => {
      if ((g as any).key === key) return true;
      const gsids = g.section_ids && g.section_ids.length ? g.section_ids : (g.section_id != null ? [g.section_id] : []);
      return wantSids.length > 0 && wantSids.every((s) => gsids.includes(s)) && gsids.length === wantSids.length;
    };
    const hit = allStores.find(byKey);
    if (hit) return hit;
    // 合算店舗 (山城屋など): worker が個別 section 店舗を返す場合は client 合算。
    if (wantSids.length > 1) {
      const parts = allStores.filter((g) => {
        const gs = g.section_ids && g.section_ids.length ? g.section_ids : (g.section_id != null ? [g.section_id] : []);
        return gs.length === 1 && wantSids.includes(gs[0]);
      });
      if (parts.length) {
        return {
          name: def?.label || key, section_ids: wantSids,
          metrics: aggregateMetrics(parts.map((p) => p.metrics)),
          prior: parts.some((p) => p.prior) ? aggregateMetrics(parts.map((p) => p.prior!).filter(Boolean)) : null,
          market_rent_missing: parts.some((p) => p.market_rent_missing),
          roumu_pending: parts.some((p) => p.roumu_pending),
          no_hours: parts.every((p) => p.no_hours),
        };
      }
    }
    // section 単一指定で worker が section_id だけ持つ行を返す場合
    if (wantSids.length === 1) {
      return allStores.find((g) => g.section_id === wantSids[0] || (g.section_ids || []).includes(wantSids[0]));
    }
    return undefined;
  }, [report, allStores]);

  // スコープ (全社/グループ/単店) の合算 metrics を解決。
  const resolveScope = useCallback((key: string): BreakevenStore | undefined => {
    if (!report) return undefined;
    if (key === 'company') {
      if (report.company) return report.company;
      // フォールバック: 全店舗 + 非店舗を合算 (EC除外は worker 側責務、ここは返却分で合算)
    }
    const def = scopeByKey(key);
    // worker が group 行 (DESIGN spec §7) を返す場合は優先採用。
    const grp = (report.groups || []).find((g) => g.group === key);
    if (grp && (grp as any).metrics) {
      return { name: def?.label || grp.label || key, metrics: (grp as any).metrics, prior: (grp as any).prior ?? null };
    }
    if (def && def.isStore) return resolveSingleStore(key);
    // グループ集計: 配下店舗を client 合算。
    if (def) {
      const parts = def.stores.map((sd) => resolveSingleStore(sd.key)).filter((s): s is BreakevenStore => !!s);
      if (parts.length) {
        return {
          name: def.label, section_ids: def.sectionIds,
          metrics: aggregateMetrics(parts.map((p) => p.metrics)),
          prior: parts.some((p) => p.prior) ? aggregateMetrics(parts.map((p) => p.prior!).filter(Boolean)) : null,
          market_rent_missing: parts.some((p) => p.market_rent_missing),
        };
      }
    }
    return report.company || undefined;
  }, [report, resolveSingleStore]);

  // 一覧表(マトリクス)の列データ: worker の report.groups[].stores / company から直接構築 (正確)。
  //   並び: 全社 / 店舗合計(飲食4グループ合算) / 各飲食グループ(合計→配下店舗) / 非店舗個別。
  const tableColumns: MatrixColumn[] = useMemo(() => {
    if (!report) return [];
    const byKey = new Map(report.groups.map((g) => [g.group, g]));
    const grpMissing = (g: BreakevenGroup): boolean => g.stores.some((s) => !!s.market_rent_missing);
    const sidKey = (s: BreakevenStore): string => `store:${(s.section_ids && s.section_ids[0]) ?? s.section_id ?? s.name}`;
    const cols: MatrixColumn[] = [];
    if (report.company) {
      cols.push({ key: 'company', label: '全社', kind: 'total', metrics: report.company.metrics ?? null, prior: report.company.prior ?? null, marketMissing: !!report.company.market_rent_missing });
    }
    const REST = ['soba', 'restaurant', 'cafe', 'washoku'];
    const restG = REST.map((k) => byKey.get(k)).filter((g): g is NonNullable<typeof g> => !!g && !!g.metrics);
    if (restG.length) {
      cols.push({
        key: 'stores', label: '店舗合計', kind: 'total',
        metrics: aggregateMetrics(restG.map((g) => g.metrics!)),
        prior: showPrev && restG.every((g) => g.prior) ? aggregateMetrics(restG.map((g) => g.prior!)) : null,
        marketMissing: restG.some(grpMissing),
      });
    }
    for (const k of REST) {
      const g = byKey.get(k);
      if (!g) continue;
      cols.push({ key: g.group, label: g.label || k, kind: 'group', metrics: g.metrics ?? null, prior: g.prior ?? null, marketMissing: grpMissing(g) });
      for (const s of g.stores) cols.push({ key: sidKey(s), label: s.name, kind: 'store', metrics: s.metrics, prior: s.prior ?? null, marketMissing: !!s.market_rent_missing });
    }
    const ns = byKey.get('nonstore');
    if (ns) for (const s of ns.stores) cols.push({ key: sidKey(s), label: s.name, kind: 'nonstore', metrics: s.metrics, prior: s.prior ?? null, marketMissing: !!s.market_rent_missing });
    return cols;
  }, [report, showPrev]);

  // 表示対象 (ドリル中なら店舗、なければスコープ)
  const targetKey = drillStore || scopeKey;
  const targetStore = drillStore ? resolveSingleStore(drillStore) : resolveScope(scopeKey);
  const targetDef = scopeByKey(targetKey) || activeScope;
  const targetLabel = targetStore?.name || targetDef.label;

  const derived = targetStore ? derive(targetStore.metrics, rent, !!targetStore.market_rent_missing) : null;
  const priorDerived = targetStore?.prior && showPrev
    ? derive(targetStore.prior, rent, !!targetStore.market_rent_missing) : null;

  // 店舗ランキング行 (スコープ配下の店舗。ドリル中も同じスコープの配下を出す)
  const rankRows: StoreRankRow[] = useMemo(() => {
    if (!report) return [];
    const rows: StoreRankRow[] = [];
    for (const sd of activeScope.stores) {
      const st = resolveSingleStore(sd.key);
      if (!st) continue;
      const d = derive(st.metrics, rent, !!st.market_rent_missing);
      const pd = showPrev && st.prior ? derive(st.prior, rent, !!st.market_rent_missing) : null;
      const stopped = d.sales === 0 && d.fixedCost === 0;
      rows.push({
        key: sd.key, name: sd.name,
        sales: d.sales, breakeven: d.breakeven ?? 0,
        achievement: d.achievement, safety: d.safety,
        priorAchievement: pd?.achievement ?? null,
        stopped, marketMissing: !!st.market_rent_missing,
      });
    }
    return rows;
  }, [report, activeScope, resolveSingleStore, rent, showPrev]);

  const market = rent === 'market';

  // 警告: 市場家賃未投入
  const marketWarnings = useMemo(() => {
    const w = [...(report?.warnings || [])];
    if (market && targetStore?.market_rent_missing) {
      w.unshift(`「${targetLabel}」は当月の市場家賃(月末仕訳)が未投入のため、市場家賃版の指標(営業利益・損益分岐点・安全余裕率・CF必要売上)は「未確定」と表示しています。実質家賃に切り替えると確定値が見られます。`);
    }
    return w;
  }, [report, market, targetStore, targetLabel]);

  // KPI カード定義
  const cards = useMemo(() => {
    if (!derived) return [];
    const d = derived;
    const opAccent = d.operatingProfit == null ? SUB : d.operatingProfit >= 0 ? GREEN : ORANGE;
    const safeAccent = d.safety == null ? SUB : d.safety >= 0 ? GREEN : ORANGE;
    const cfGap = d.cfRequired != null ? d.sales - d.cfRequired : null;
    // 市場損益 = 営業利益(市場家賃版)。real モードでも参考値として market 版を表示。
    const mp = targetStore?.market_rent_missing ? null : (targetStore?.metrics.営業利益_市場 ?? null);
    const mpColor = mp == null ? SUB : mp >= 0 ? GREEN_DK : RED;
    return [
      {
        label: '営業利益', Icon: MdTrendingUp, accent: opAccent,
        value: d.operatingProfit == null ? '未確定' : yenShort(d.operatingProfit),
        valueColor: opAccent === GREEN ? GREEN_DK : opAccent === ORANGE ? RED : SUB,
        sub: showPrev ? yoyText(yoyRatio(d.operatingProfit, priorDerived?.operatingProfit)) : `経常利益 ${yenShort(d.ordinaryProfit)}`,
      },
      {
        label: market ? '市場損益' : '市場損益(参考)', Icon: MdSwapHoriz, accent: PURPLE,
        value: mp == null ? '未確定' : yenShort(mp),
        valueColor: mpColor,
        sub: `家賃減額差 ${targetStore?.market_rent_missing ? '未確定' : yenShort(d.rentDiff)}`,
      },
      {
        label: '損益分岐点売上', Icon: MdShowChart, accent: BLUE,
        value: d.breakeven == null ? '未確定' : yenShort(d.breakeven),
        valueColor: BLUE,
        sub: d.achievement == null ? '達成率 —' : `達成率 ${pct(d.achievement, 0)}`,
      },
      {
        label: '安全余裕率', Icon: MdQueryStats, accent: safeAccent,
        value: d.safety == null ? '未確定' : pct(d.safety),
        valueColor: safeAccent === GREEN ? GREEN_DK : safeAccent === ORANGE ? RED : SUB,
        sub: showPrev && priorDerived?.safety != null && d.safety != null
          ? `前年 ${pct(priorDerived.safety)} (${ptDiff(d.safety, priorDerived.safety)})`
          : `限界利益率 ${pct(d.marginRatio)}`,
      },
      {
        label: 'CF必要売上', Icon: MdSavings, accent: cfGap != null && cfGap >= 0 ? GREEN : ORANGE,
        value: d.cfRequired == null ? '未確定' : yenShort(d.cfRequired),
        valueColor: cfGap != null && cfGap >= 0 ? GREEN_DK : RED,
        sub: cfGap == null ? `CF ${yenShort(d.cf)}`
          : cfGap >= 0 ? `達成 (CF ${yenShort(d.cf)})` : `黒字化まで あと ${yenShort(-cfGap)}`,
      },
      {
        label: '人時生産性', Icon: MdSpeed, accent: '#0d9488',
        value: d.laborProductivity == null ? '—' : `${yenFull(Math.round(d.laborProductivity))}`,
        valueColor: '#0d7a72',
        sub: d.laborProductivity == null
          ? (targetStore?.no_hours ? '工数対象外' : targetStore?.roumu_pending ? '工数未確定' : '勤務時間なし')
          : showPrev
            ? `/時 ${yoyText(yoyRatio(d.laborProductivity, priorDerived?.laborProductivity))}`
            : `/時 ・ ${Math.round(d.workHours).toLocaleString('ja-JP')}h`,
      },
    ];
  }, [derived, priorDerived, market, showPrev, targetStore]);

  return (
    <div style={S.page}>
      <style>{`
        .bx-row:hover td { background: #eef5fc !important; }
        .bx-matrix-wrap::-webkit-scrollbar { height: 11px; width: 11px; }
        .bx-matrix-wrap::-webkit-scrollbar-thumb { background: #cfcbc4; border-radius: 6px; border: 2px solid #faf9f7; }
        .bx-matrix-wrap::-webkit-scrollbar-track { background: #f4f2ef; }
        @media print {
          .pa-no-print { display: none !important; }
          .bx-matrix-wrap { max-height: none !important; overflow: visible !important; }
          @page { size: A4 landscape; margin: 8mm; }
        }
      `}</style>
      <Container>
        <div style={{ paddingTop: '18px' }} className="pa-no-print">
          <Breadcrumbs links={[{ title: 'FREEEMAN', url: '/' }, { title: '経営管理' }, { title: '損益分岐点分析' }]} />
        </div>
        <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <MdTrendingUp size={26} color={GREEN_DK} />
          <PageTitle>損益分岐点分析</PageTitle>
        </div>
        <Note>
          売上が損益分岐点を超えると黒字。安全余裕率 =（売上 − 損益分岐点）÷ 売上。
          家賃は「市場家賃（管理上の想定賃料）」表示が初期値です。実質家賃（地代家賃）にも切替できます。
          {TERM_LABEL}。
        </Note>

        {/* ── 操作バー ── */}
        <div style={{ marginTop: '14px' }} className="pa-no-print">
          <CardBase>
            <div style={S.bar}>
              <HStack gap={1.5} alignItems="end" wrap="wrap">
                <div>
                  <div style={S.ctlLabel}>表示</div>
                  <div style={{ display: 'inline-flex', border: '1px solid #d4d2d0', borderRadius: 8, overflow: 'hidden' }}>
                    {([['analysis', '分析'], ['table', '一覧表']] as ['analysis' | 'table', string][]).map(([v, lbl], i) => (
                      <button key={v} type="button" style={{ ...SEG(viewMode === v, GREEN_DK), borderLeft: i ? '1px solid #e5e4e2' : 'none' }}
                        onClick={() => setViewMode(v)}>{lbl}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={S.ctlLabel}>期間粒度</div>
                  <div style={{ display: 'inline-flex', border: '1px solid #d4d2d0', borderRadius: 8, overflow: 'hidden' }}>
                    {([['month', '単月'], ['half', '半期'], ['full', '通期']] as [BreakevenPeriod, string][]).map(([p, lbl], i) => (
                      <button key={p} type="button" style={{ ...SEG(period === p), borderLeft: i ? '1px solid #e5e4e2' : 'none' }}
                        onClick={() => { setPeriod(p); setDrillStore(null); }}>{lbl}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={S.ctlLabel}>基準月</div>
                  <SelectBox name="fm" value={String(fm)} width="small" disabled={period === 'full'}
                    options={FISCAL_MONTHS.map((m) => ({ name: m.label, value: String(m.fm) }))}
                    onChange={(e) => { setFm(Number((e.target as HTMLSelectElement).value)); setDrillStore(null); }} />
                </div>
                {viewMode === 'analysis' && (
                  <div>
                    <div style={S.ctlLabel}>家賃</div>
                    <div style={{ display: 'inline-flex', gap: 6 }}>
                      <span style={CHIP(rent === 'market', PURPLE)} onClick={() => setRent('market')}>市場家賃</span>
                      <span style={CHIP(rent === 'real')} onClick={() => setRent('real')}>実質家賃</span>
                    </div>
                  </div>
                )}
                <div>
                  <div style={S.ctlLabel}>前年比</div>
                  <span style={CHIP(showPrev)} onClick={() => setShowPrev((v) => !v)}>
                    {showPrev ? '前年比 ON' : '前年比 OFF'}
                  </span>
                </div>
                <Button onClick={load} disabled={loading}>
                  {loading ? <InlineSpinner isLoading /> : <MdRefresh />} 再読込
                </Button>
              </HStack>

              {/* グループ選択チップ列 (分析ビューのみ) */}
              {viewMode === 'analysis' ? (
                <div style={{ display: 'flex', gap: 7, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  {SCOPES.map((sc, i) => {
                    const isHQDivider = sc.key === 'stores';
                    return (
                      <React.Fragment key={sc.key}>
                        {isHQDivider && <span style={{ color: '#d4d2d0', margin: '0 2px' }}>|</span>}
                        <span style={CHIP(scopeKey === sc.key && !drillStore)}
                          onClick={() => { setScopeKey(sc.key); setDrillStore(null); }}>{sc.label}</span>
                      </React.Fragment>
                    );
                  })}
                </div>
              ) : (
                <div style={{ marginTop: 10, fontSize: '0.75rem', color: SUB }}>
                  全部門を損益カードで一覧。「前年比 ON」で前年も表示。各カードの「全指標を表示」で詳細を展開できます。
                </div>
              )}
            </div>
          </CardBase>
        </div>

        {error && <div style={{ marginTop: 12 }}><MessageBlock error>{error}</MessageBlock></div>}

        {/* 市場家賃未投入 等の警告 */}
        {marketWarnings.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <MessageBlock notice>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <MdWarning size={18} color={ORANGE} style={{ flexShrink: 0, marginTop: 2 }} />
                <div>
                  {marketWarnings.map((w, i) => <div key={i} style={{ marginBottom: i < marketWarnings.length - 1 ? 4 : 0 }}>{w}</div>)}
                </div>
              </div>
            </MessageBlock>
          </div>
        )}

        {loading && !report ? (
          <div style={{ padding: 60, textAlign: 'center' }}><InlineSpinner isLoading /> 読み込み中…</div>
        ) : report && viewMode === 'table' ? (
          <div style={{ marginTop: 16 }}>
            <BreakevenCards columns={tableColumns} showPrev={showPrev} periodLabel={report.range?.label} />
          </div>
        ) : !derived ? (
          <div style={{ marginTop: 16 }}>
            <CardBase><div style={{ padding: 40, textAlign: 'center', color: SUB }}>
              「{targetLabel}」のデータが取得できませんでした。
            </div></CardBase>
          </div>
        ) : (
          <>
            {/* ドリル中の戻りパンくず */}
            {drillStore && (
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }} className="pa-no-print">
                <span style={CHIP(false)} onClick={() => setDrillStore(null)}>← {activeScope.label} に戻る</span>
                <span style={{ fontSize: '0.8125rem', color: '#5c554f', fontWeight: 700 }}>{targetLabel}</span>
              </div>
            )}

            {/* ── KPIカード層 ── */}
            <div style={S.kpiRow}>
              {cards.map((c, i) => (
                <div key={i} style={S.kpiCard(c.accent)}>
                  <div style={S.kpiLabel}><c.Icon size={14} color={c.accent} />{c.label}</div>
                  <div style={{ ...S.kpiValue, color: c.valueColor }}>{c.value}</div>
                  <div style={S.kpiSub}>{c.sub}</div>
                </div>
              ))}
            </div>

            {/* ── 主役: 損益分岐点チャート ── */}
            <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'minmax(0, 1.85fr) minmax(0, 1fr)', gap: 12 }}>
              <CardBase>
                <div style={S.cardPad}>
                  <div style={S.sectionTitle}>
                    <MdShowChart size={15} color={market ? PURPLE : BLUE} />
                    売上 vs 損益分岐点 — {targetLabel}
                    {market && <span style={{ color: PURPLE, fontWeight: 700, fontSize: '0.6875rem', border: `1px solid ${PURPLE}55`, borderRadius: 4, padding: '1px 6px' }}>市場家賃</span>}
                  </div>
                  {derived.breakeven == null ? (
                    <div style={{ padding: 28, textAlign: 'center', color: ORANGE, fontWeight: 700 }}>
                      <MdWarning size={18} /> 市場家賃が未投入のため損益分岐点は未確定です。
                    </div>
                  ) : (
                    <HorizontalBreakevenGauge
                      sales={derived.sales} breakeven={derived.breakeven}
                      priorBreakeven={showPrev ? priorDerived?.breakeven ?? null : null}
                      market={market}
                    />
                  )}
                  <div style={{ marginTop: 8 }}>
                    <SalesDecomposition
                      sales={derived.sales} variableCost={derived.material}
                      fixedCost={derived.fixedCost} operatingProfit={derived.operatingProfit ?? 0}
                      market={market}
                    />
                  </div>
                </div>
              </CardBase>
              <CardBase>
                <div style={S.cardPad}>
                  <div style={S.sectionTitle}><MdQueryStats size={15} color={GREEN_DK} />安全余裕率</div>
                  <SafetyArcGauge ratio={derived.safety} marginRatio={derived.marginRatio} fixedCost={derived.fixedCost} market={market} />
                </div>
              </CardBase>
            </div>

            {/* ── CVP 図 ── */}
            <div style={{ marginTop: 12 }}>
              <CardBase>
                <div style={S.cardPad}>
                  <div style={S.sectionTitle}><MdShowChart size={15} color={ORANGE} />CVP（損益分岐点）図</div>
                  {derived.breakeven == null ? (
                    <div style={{ padding: 24, textAlign: 'center', color: SUB }}>市場家賃未投入のため表示できません（実質家賃に切替可）。</div>
                  ) : (
                    <CvpChart sales={derived.sales} fixedCost={derived.fixedCost}
                      variableRatio={derived.costRatio} breakeven={derived.breakeven} market={market} />
                  )}
                </div>
              </CardBase>
            </div>

            {/* ── 店舗別 達成度ランキング ── */}
            {rankRows.length > 1 && (
              <div style={{ marginTop: 12 }} className="pa-no-print">
                <CardBase>
                  <div style={S.cardPad}>
                    <div style={S.sectionTitle}>
                      <MdAccountBalance size={15} color={BLUE} />
                      店舗別 達成度ランキング — {activeScope.label}（行クリックでドリル）
                    </div>
                    <StoreRankBars rows={rankRows} showPrior={showPrev} market={market}
                      onPick={(k) => setDrillStore(k)} />
                  </div>
                </CardBase>
              </div>
            )}

            {/* ── 損益構造 内訳テーブル (折りたたみ) ── */}
            <PlBreakdownTable d={derived} prior={priorDerived} showPrev={showPrev} market={market} label={targetLabel} />
          </>
        )}

        <style>{`@media print {
          .pa-no-print { display: none !important; }
          body { background: #fff !important; }
        }`}</style>
      </Container>
    </div>
  );
};

// 前年比テキスト
function yoyText(r: number | null): string {
  if (r == null) return '前年比 —';
  const s = r >= 0 ? '+' : '▲';
  return `前年比 ${s}${Math.abs(r * 100).toFixed(1)}%`;
}
function ptDiff(cur: number, prior: number): string {
  const d = (cur - prior) * 100;
  return `${d >= 0 ? '+' : '▲'}${Math.abs(d).toFixed(1)}pt`;
}

// ---------------------------------------------------------------------------
// 損益構造 内訳テーブル (折りたたみ)
// ---------------------------------------------------------------------------
const PlBreakdownTable: React.FC<{
  d: Derived; prior: Derived | null; showPrev: boolean; market: boolean; label: string;
}> = ({ d, prior, showPrev, market }) => {
  const [open, setOpen] = useState(false);
  const tdN = (v: number | null, color?: string): React.CSSProperties => ({
    textAlign: 'right', fontVariantNumeric: 'tabular-nums', padding: '6px 12px',
    borderBottom: '1px solid #f1f0ee', whiteSpace: 'nowrap', color, fontWeight: 600,
  });
  const tdL: React.CSSProperties = { padding: '6px 12px', borderBottom: '1px solid #f1f0ee', whiteSpace: 'nowrap' };
  const th: React.CSSProperties = {
    padding: '8px 12px', fontSize: '0.6875rem', fontWeight: 700, color: '#6b6560',
    borderBottom: '1.5px solid #e5e4e2', background: '#fafaf8', textAlign: 'right',
  };
  type Row = { label: string; cur: number | null; prior: number | null; ratio?: boolean; emph?: boolean; sign?: boolean };
  const rows: Row[] = [
    { label: '売上', cur: d.sales, prior: prior?.sales ?? null, emph: true },
    { label: '原材料（変動費）', cur: d.material, prior: prior?.material ?? null },
    { label: '原価率', cur: d.costRatio, prior: prior?.costRatio ?? null, ratio: true },
    { label: '粗利益（限界利益）', cur: d.grossProfit, prior: prior?.grossProfit ?? null, emph: true },
    { label: `一般管理費（固定費${market ? '・市場家賃' : ''}）`, cur: d.fixedCost, prior: prior?.fixedCost ?? null },
    { label: market ? '市場家賃' : '実質家賃（地代家賃）', cur: market ? d.rentMarket : d.rentReal, prior: market ? (prior?.rentMarket ?? null) : (prior?.rentReal ?? null) },
    { label: '家賃減額差（市場−実質）', cur: d.rentDiff, prior: prior?.rentDiff ?? null },
    { label: '減価償却費（内数）', cur: d.depreciation, prior: prior?.depreciation ?? null },
    { label: market ? '営業利益（市場損益）' : '営業利益', cur: d.operatingProfit, prior: prior?.operatingProfit ?? null, emph: true, sign: true },
    { label: '経常利益', cur: d.ordinaryProfit, prior: prior?.ordinaryProfit ?? null, sign: true },
    { label: '銀行返済（元本）', cur: d.bankRepayment, prior: prior?.bankRepayment ?? null },
    { label: 'CF（経常+償却−返済）', cur: d.cf, prior: prior?.cf ?? null, sign: true },
    { label: '損益分岐点売上', cur: d.breakeven, prior: prior?.breakeven ?? null, emph: true },
    { label: 'CF必要売上', cur: d.cfRequired, prior: prior?.cfRequired ?? null },
    { label: '勤務時間（h）', cur: d.workHours || null, prior: prior?.workHours ?? null },
    { label: '人時生産性（粗利÷時間）', cur: d.laborProductivity, prior: prior?.laborProductivity ?? null },
  ];
  const fmtVal = (v: number | null, r?: boolean, sign?: boolean): string => {
    if (v == null) return r ? '—' : '未確定';
    if (r) return pct(v);
    return sign ? yenFull(v) : yenFull(v);
  };
  return (
    <div style={{ marginTop: 12 }}>
      <CardBase>
        <div style={{ padding: '10px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: '#5c554f', fontWeight: 700, fontSize: '0.8125rem' }}
          onClick={() => setOpen((v) => !v)}>
          <span style={{ fontSize: 11 }}>{open ? '▼' : '▸'}</span> 損益構造の内訳（{market ? '市場家賃' : '実質家賃'}）を{open ? '閉じる' : '開く'}
        </div>
        {open && (
          <div style={{ padding: '0 12px 14px', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem', minWidth: 420 }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }}>項目</th>
                  <th style={th}>当期</th>
                  {showPrev && <th style={th}>前年同期</th>}
                  {showPrev && <th style={th}>前年比</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const valColor = r.sign && r.cur != null ? (r.cur >= 0 ? GREEN_DK : RED) : undefined;
                  const yr = !r.ratio ? yoyRatio(r.cur, r.prior) : (r.cur != null && r.prior != null ? r.cur - r.prior : null);
                  return (
                    <tr key={i} style={r.emph ? { background: '#faf9f7' } : undefined}>
                      <td style={{ ...tdL, fontWeight: r.emph ? 700 : 500 }}>{r.label}</td>
                      <td style={tdN(r.cur, valColor)}>{fmtVal(r.cur, r.ratio, r.sign)}</td>
                      {showPrev && <td style={tdN(r.prior, undefined)}>{fmtVal(r.prior, r.ratio, r.sign)}</td>}
                      {showPrev && (
                        <td style={tdN(null, yr == null ? SUB : (yr >= 0 ? GREEN_DK : RED))}>
                          {yr == null ? '—' : r.ratio ? ptDiff(r.cur as number, r.prior as number) : `${yr >= 0 ? '+' : '▲'}${Math.abs(yr * 100).toFixed(1)}%`}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardBase>
    </div>
  );
};
